import { useState } from "react";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import Spotlight from "../components/Spotlight";
import SectionTransition from "../components/SectionTransition";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const serviceOptions = [
  "Software Development",
  "Web Development",
  "E-Commerce Development",
  "Digital Marketing",
  "Custom Product",
  "Internship Inquiry",
  "Other",
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", service_interest: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      setSuccess(true);
      setForm({ name: "", email: "", service_interest: "", message: "" });
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <Spotlight className="bg-neutral-950 grid-pattern pt-32 pb-20 md:pt-44 md:pb-28">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-medium tracking-wider uppercase text-blue-400 mb-4 animate-fade-in-up">Get in Touch</p>
            <h1
              data-testid="contact-heading"
              className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] text-white mb-6 animate-fade-in-up animation-delay-200"
            >
              Connect with us
            </h1>
            <p className="text-lg md:text-xl text-slate-400 leading-relaxed animate-fade-in-up animation-delay-400">
              Have a project in mind or want to learn more about our services? 
              We would love to hear from you.
            </p>
          </div>
        </div>
      </Spotlight>

      {/* Contact Section */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Info */}
            <div className="lg:col-span-2">
              <SectionTransition>
                <h2 className="text-3xl font-semibold tracking-tight text-slate-900 mb-6">
                  Let Us Talk
                </h2>
                <p className="text-slate-600 leading-relaxed mb-10">
                  Reach out to us for project discussions, partnerships, internship 
                  inquiries, or any questions. Our team will get back to you promptly.
                </p>

                <div className="space-y-6">
                  <a
                    href="mailto:wecodxtech@gmail.com"
                    data-testid="contact-info-email"
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center shrink-0 group-hover:bg-blue-100 transition-colors">
                      <Mail size={20} className="text-blue-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Email</p>
                      <p className="text-sm text-slate-500">wecodxtech@gmail.com</p>
                    </div>
                  </a>

                  <a
                    href="tel:+917810097316"
                    data-testid="contact-info-phone"
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center shrink-0 group-hover:bg-blue-100 transition-colors">
                      <Phone size={20} className="text-blue-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Phone</p>
                      <p className="text-sm text-slate-500">+91 7810097316</p>
                    </div>
                  </a>

                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                      <MapPin size={20} className="text-blue-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Location</p>
                      <p className="text-sm text-slate-500">India</p>
                    </div>
                  </div>
                </div>
              </SectionTransition>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <SectionTransition delay={0.2}>
                {success ? (
                  <div data-testid="contact-success" className="p-10 rounded-2xl border border-green-200 bg-green-50 text-center">
                    <CheckCircle2 size={48} className="text-green-600 mx-auto mb-4" strokeWidth={1.5} />
                    <h3 className="text-2xl font-semibold text-slate-900 mb-2">Message Sent</h3>
                    <p className="text-slate-600 mb-6">Thank you for reaching out. We will get back to you within 24 hours.</p>
                    <Button
                      onClick={() => setSuccess(false)}
                      variant="outline"
                      className="rounded-full"
                      data-testid="contact-send-another-btn"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="p-8 md:p-10 rounded-2xl border border-slate-200" data-testid="contact-form">
                    <h3 className="text-2xl font-semibold text-slate-900 mb-8">Send Us a Message</h3>

                    <div className="space-y-6">
                      <div>
                        <Label htmlFor="name" className="text-sm font-medium text-slate-700 mb-2 block">
                          Full Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="name"
                          data-testid="contact-name-input"
                          placeholder="Your name"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          className="rounded-lg"
                        />
                      </div>

                      <div>
                        <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-2 block">
                          Email Address <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          data-testid="contact-email-input"
                          placeholder="you@example.com"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className="rounded-lg"
                        />
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-slate-700 mb-2 block">
                          Service Interest
                        </Label>
                        <Select
                          value={form.service_interest}
                          onValueChange={(val) => setForm({ ...form, service_interest: val })}
                        >
                          <SelectTrigger data-testid="contact-service-select" className="rounded-lg">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            {serviceOptions.map((opt) => (
                              <SelectItem key={opt} value={opt} data-testid={`service-option-${opt.toLowerCase().replace(/\s+/g, '-')}`}>
                                {opt}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="message" className="text-sm font-medium text-slate-700 mb-2 block">
                          Message <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                          id="message"
                          data-testid="contact-message-input"
                          placeholder="Tell us about your project or inquiry..."
                          rows={5}
                          value={form.message}
                          onChange={(e) => setForm({ ...form, message: e.target.value })}
                          className="rounded-lg resize-none"
                        />
                      </div>

                      {error && (
                        <p data-testid="contact-error" className="text-sm text-red-600">{error}</p>
                      )}

                      <Button
                        type="submit"
                        data-testid="contact-submit-btn"
                        disabled={loading}
                        className="w-full rounded-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Sending...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Send size={16} /> Send Message
                          </span>
                        )}
                      </Button>
                    </div>
                  </form>
                )}
              </SectionTransition>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
